import { useEffect } from 'react';
import Helmet from 'react-helmet';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';

import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { BodyL } from '@sorare/core/src/atoms/typography';
import { ORIGIN } from '@sorare/core/src/config';
import { NBA_PLAY } from '@sorare/core/src/constants/__generated__/routes';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';

import { EarlyAccess } from 'components/landing/EarlyAccess';

import competeCardsAvif from './assets/compete/cards.avif';
import competeCards from './assets/compete/cards.png';
import fieldAvif from './assets/compete/field.avif';
import field from './assets/compete/field.jpg';
import heroAvif from './assets/hero/hero.avif';
import hero from './assets/hero/hero.jpg';
import logo from './assets/hero/logo.svg';
import metadataImage from './assets/public/metadataImage.jpg';
import scoutBackgroundAvif from './assets/scout/background.avif';
import scoutBackground from './assets/scout/background.jpg';
import scoutPlayersAvif from './assets/scout/players.avif';
import scoutPlayers from './assets/scout/players.png';
import winCardsAvif from './assets/win/cards.avif';
import winCards from './assets/win/cards.png';

const Hero = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--quadruple-unit);
  margin-bottom: calc(8 * var(--unit));
  min-height: calc(var(--100vh) - var(--current-stack-height));
  justify-content: center;
  background-image: image-set(
    url(${heroAvif}) type('image/avif'),
    url(${hero}) type('image/jpeg')
  );
  background-position: center;
  background-size: cover;
`;
const Title = styled.h1`
  font-size: var(--fs-landing-title);
`;

const messages = defineMessages({
  metadatasTitle: {
    id: 'Landing.NBA.metadatasTitle',
    defaultMessage:
      'Play fantasy basketball with ownable NBA player cards - Sorare',
  },
  metadatasDescription: {
    id: 'Landing.NBA.metadatasDescription',
    defaultMessage:
      "Play Sorare's free fantasy basketball game. Collect, buy, sell & compete with ownable digital player cards from all 30 NBA teams to win cash, ETH, tickets, more",
  },
});

const assets = {
  scout: {
    background: {
      avif: scoutBackgroundAvif,
      pictureUrl: scoutBackground,
    },
    players: { avif: scoutPlayers, pictureUrl: scoutPlayersAvif },
  },
  compete: {
    field: {
      avif: fieldAvif,
      pictureUrl: field,
    },
    cards: { avif: competeCardsAvif, pictureUrl: competeCards },
  },
  win: {
    cards: {
      avif: winCardsAvif,
      pictureUrl: winCards,
    },
  },
};

const NbaParis2025 = () => {
  const { signUp } = useConnectionContext();
  const isLoggedIn = useIsLoggedIn();
  const { formatMessage, locale } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const track = useEvents();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: metadataImage,
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );

  useEffect(() => {
    if (!isLoggedIn) {
      track('View Homepage Disconnected', {
        landing_variant: 'NBA Paris 2025',
      });
    }
  }, [isLoggedIn, track]);

  if (!isLoggedIn) {
    return (
      <>
        <Helmet>
          <link rel="canonical" href={`${ORIGIN}${locale}`} />
        </Helmet>
        <EarlyAccess
          assets={assets}
          leaderboards={[
            So5LeaderboardType.NBA_LIMITED_CHAMPION,
            So5LeaderboardType.NBA_UNIQUE_CHAMPION,
            So5LeaderboardType.NBA_RARE_CHAMPION,
          ]}
          header={
            <Hero className="text-center">
              <img src={logo} alt="" width="263" height="291" />
              <div>
                <Title>
                  <FormattedMessage
                    id="nba.paris2025.title"
                    defaultMessage="Great job finding our code"
                  />
                </Title>
                <BodyL>
                  <FormattedMessage
                    id="nba.paris2025.description"
                    defaultMessage="To enter the NBA Paris tickets raffle, open your free starter pack and create your first lineup."
                  />
                </BodyL>
              </div>
              <Button onClick={signUp} color="secondary" size="large">
                <FormattedMessage
                  id="nba.paris2025.cta"
                  defaultMessage="Enter now"
                />
              </Button>
            </Hero>
          }
        />
      </>
    );
  }

  return <Navigate to={NBA_PLAY} replace />;
};

export default NbaParis2025;
